import type { QueuedMutation } from '../queue/types';

/**
 * Marks a failed mutation the server refused because the data it edits changed
 * since the edit was made (a stale write). A handler puts this at the front of the
 * `error` it returns; the failed-items panel then says what happened and does not
 * offer a Retry that can only be refused again: the same payload names the same
 * out-of-date version, so redoing the change on the latest data is the only fix.
 */
export const STALE_WRITE_ERROR_PREFIX = 'stale-write: ';

/** The error text for a stale-write refusal: the marker plus the server's own wording. */
export function staleWriteError(serverMessage: string): string {
  return `${STALE_WRITE_ERROR_PREFIX}${serverMessage}`;
}

/** True when `error` is a stale-write refusal (see STALE_WRITE_ERROR_PREFIX). */
export function isStaleWriteError(error: string | null | undefined): boolean {
  return typeof error === 'string' && error.startsWith(STALE_WRITE_ERROR_PREFIX);
}

/** The server's wording of a stale-write refusal, without the marker. */
export function staleWriteMessage(error: string): string {
  return error.startsWith(STALE_WRITE_ERROR_PREFIX) ? error.slice(STALE_WRITE_ERROR_PREFIX.length) : error;
}

/** True when this failed mutation was refused as a stale write (a retry cannot succeed). */
export function isStaleWriteFailure(mutation: QueuedMutation): boolean {
  return isStaleWriteError(mutation.error);
}
